import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { MIN_QUERY } from './searchFormat';

// One search, as the bar and the results page ask for it: POST /api/search with the term in the BODY (a URL would end up in the
// backend's access logs). The term is held in memory only: never in storage, never remembered. A stale answer is dropped, a failed
// one is "error" (the page says so; it never shows "no results" for a search that did not run).
//   idle     nothing asked (the term is empty or shorter than the minimum)
//   loading  waiting (the previous answer stays on screen meanwhile)
//   ready    data = the server's answer; query = the term it answers
//   error    the search could not run
export function useSearch(query, { mode = 'bar', delay = 250 } = {}) {
  const [state, setState] = useState({ status: 'idle', data: null, query: '' });
  useEffect(() => {
    const q = String(query || '').trim();
    if (q.length < MIN_QUERY) { setState({ status: 'idle', data: null, query: '' }); return undefined; }
    let cancelled = false;
    const ctrl = typeof AbortController === 'undefined' ? null : new AbortController();
    setState((s) => ({ status: 'loading', data: s.data, query: s.query }));
    const run = async () => {
      try {
        const res = await apiFetch('/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q, mode }),
          signal: ctrl ? ctrl.signal : undefined,
        });
        const json = await res.json();
        if (!res.ok || !json || !json.ok) throw new Error('search');
        if (!cancelled) setState({ status: 'ready', data: json, query: q });
      } catch (err) {
        if (!cancelled && !(err && err.name === 'AbortError')) setState({ status: 'error', data: null, query: q });
      }
    };
    const timer = setTimeout(run, delay);
    return () => { cancelled = true; clearTimeout(timer); if (ctrl) ctrl.abort(); };
  }, [query, mode, delay]);
  return state;
}
